import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { VideoCard } from "@/components/VideoCard";
import { useAuth } from "@/context/AuthContext";
import { Lock, ArrowLeft, Calendar, MapPin, Clock, Library as LibIcon, FileText, Copyright, Archive, Film, Layers, BadgeCheck, User, Video as VideoIcon, Building2, ShieldQuestion, AlertTriangle, Info } from "lucide-react";

const RIGHTS_INTERIM = "The Archive includes embedded or linked material from publicly available sources as well as material provided directly by contributors. Copyright remains with the respective rights holders. Rights, provenance, and attribution are being researched and documented as part of the archival process.";

const CONTRIB_INPUT = "w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-3 py-2.5 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm";

export default function Watch() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState({});
  const [loading, setLoading] = useState(true);
  const [showContrib, setShowContrib] = useState(false);
  const [contrib, setContrib] = useState({ name: "", email: "", subject: "correction", message: "" });
  const [contribBusy, setContribBusy] = useState(false);
  const [contribDone, setContribDone] = useState(false);

  const submitContrib = async (e) => {
    e.preventDefault();
    if (!contrib.message.trim()) return;
    setContribBusy(true);
    try {
      await api.post("/contributions", { video_id: id, ...contrib });
      setContribDone(true);
      setContrib({ name: "", email: "", subject: "correction", message: "" });
    } catch (err) {
      toast.error("Could not submit — please try again.");
    } finally { setContribBusy(false); }
  };

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/videos/${id}`).then(({ data }) => setVideo(data)).catch(() => setVideo(null)).finally(() => setLoading(false));
    api.get(`/videos/${id}/related`).then(({ data }) => setRelated(data)).catch(() => setRelated({}));
  }, [id]);

  if (loading) return <div className="min-h-screen grain"><Navbar /><p className="text-center text-[#8A8F98] py-32">Loading…</p></div>;
  if (!video) return <div className="min-h-screen grain"><Navbar /><p className="text-center text-[#8A8F98] py-32">Video not found.</p></div>;

  const locked = video.locked;
  const trial = video.trial;
  const reason = video.reason;
  const signinReason = reason === "signin";
  const unavailable = reason === "unavailable" || video.available === false;
  const sourceIssue = !locked && !unavailable && video.source_status && video.source_status !== "Available";
  const underReview = video.source_status === "Under review";
  const showOriginalTitle = video.original_title && video.original_title.trim() &&
    video.original_title.trim().toLowerCase() !== (video.title || "").trim().toLowerCase();

  const relatedSections = [
    { key: "same_seminar", label: "From the Same Seminar or Location", items: related.same_seminar },
    { key: "same_technique", label: "Related Techniques & Subjects", items: related.same_technique },
    { key: "same_year", label: `Also from ${video.year || "this era"}`, items: related.same_year },
  ].filter((s) => s.items && s.items.length > 0);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-6xl py-8 md:py-10">
        <Link to="/library" data-testid="back-to-library" className="inline-flex items-center gap-2 text-[#8A8F98] hover:text-[#C5A059] mb-8 text-sm transition-colors">
          <ArrowLeft size={16} /> Back to library
        </Link>

        {/* Free-preview banner (unlocked via free views) */}
        {!locked && trial && (
          <div data-testid="free-preview-banner" className="flex items-center justify-between gap-4 border border-[#C5A059]/30 bg-[#15130d] px-5 py-3 mb-5">
            <p className="text-sm text-[#EBE9E1]">
              <span className="text-[#C5A059] font-medium">Free preview</span> · {trial.remaining} of {trial.limit} free videos remaining
            </p>
            <Link to="/register" className="text-sm text-[#C5A059] hover:text-[#D6B570] whitespace-nowrap">Create free account →</Link>
          </div>
        )}

        <div className="aspect-video border border-white/10 bg-[#121418] overflow-hidden mb-10 relative">
          {unavailable ? (
            <div data-testid="video-unavailable" className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-[#0f1115]">
              <Archive size={38} className="text-[#C5A059] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl text-[#EBE9E1] mb-3">Catalogued in the Archive</h3>
              <p className="text-[#8A8F98] max-w-md">
                This recording is documented in the collection but is not yet available to stream.
                Digitization and rights clearance are in progress. Its full archive record appears below.
              </p>
            </div>
          ) : locked ? (
            <div data-testid="video-locked" className="w-full h-full flex flex-col items-center justify-center text-center px-6"
              style={{ backgroundImage: video.thumb_url ? `url(${video.thumb_url})` : "none", backgroundSize: "cover", backgroundPosition: "center" }}>
              <div className="absolute inset-0 bg-[#0A0B0E]/88" />
              <div className="relative">
                <Lock size={36} className="text-[#C5A059] mx-auto mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-3xl text-[#EBE9E1] mb-3">Free during our beta</h3>
                <p className="text-[#8A8F98] max-w-md mb-6">
                  Create a free account to stream the full archive — just your name, email, and dojo affiliation. No payment required.
                </p>
                <button data-testid="watch-subscribe-btn" onClick={() => navigate("/register")}
                  className="btn-primary px-8 py-3 font-medium">
                  Create Free Account
                </button>
              </div>
            </div>
          ) : video.embed_provider === "file" && video.embed_url ? (
            <video data-testid="video-player" className="w-full h-full bg-black" src={video.embed_url}
              controls playsInline poster={video.thumb_url || undefined} preload="metadata" />
          ) : sourceIssue ? (
            <div data-testid="source-unavailable" className="w-full h-full flex flex-col items-center justify-center text-center px-6 bg-[#0f1115]">
              <AlertTriangle size={36} className="text-[#C08A3E] mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl text-[#EBE9E1] mb-3">{underReview ? "Source Under Review" : "Source Currently Unavailable"}</h3>
              <p className="text-[#8A8F98] max-w-md">
                {underReview
                  ? "This recording's source is being reviewed for provenance and rights. Its archive record appears below."
                  : "The original source for this recording is not reachable at the moment. Its archive record remains catalogued below."}
              </p>
            </div>
          ) : video.embed_url ? (
            <iframe data-testid="video-player" className="w-full h-full"
              src={video.embed_url}
              title={video.title} loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#8A8F98]">Video source unavailable</div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <p className="text-sm tracking-[0.25em] uppercase text-[#C5A059]">{video.content_type || video.category || video.subject}</p>
              {video.verification_status && <VerificationBadge status={video.verification_status} />}
            </div>
            <h1 data-testid="video-title" className="font-serif text-4xl md:text-5xl text-[#EBE9E1] leading-[1.05] mb-3">{video.title}</h1>
            {showOriginalTitle && (
              <p data-testid="original-title" className="text-[#8A8F98] text-sm mb-5">
                <span className="uppercase tracking-widest text-[10px] text-[#6a6f77] mr-2">Original source title</span>
                <span className="italic">“{video.original_title}”</span>
              </p>
            )}
            <p className="text-[#EBE9E1]/80 leading-relaxed text-lg mt-3">{video.description || "No description is recorded for this archive entry."}</p>
            {video.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {video.tags.map((t) => (
                  <Link key={t} to={`/library?q=${encodeURIComponent(t)}`}
                    className="text-xs text-[#8A8F98] border border-white/10 px-3 py-1 hover:border-[#C5A059]/50 hover:text-[#C5A059] transition-colors">{t}</Link>
                ))}
              </div>
            )}
          </div>

          {/* About This Recording — archival metadata (§6) */}
          <aside data-testid="about-recording" className="border border-white/10 bg-[#121418] p-6 space-y-5 h-fit">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] pb-1">About This Recording</p>
            {video.content_type && <Meta icon={Film} label="Content Type" value={video.content_type} />}
            {Array.isArray(video.areas_of_study) && video.areas_of_study.length > 0 &&
              <Meta icon={Layers} label="Area(s) of Study" value={video.areas_of_study.join(" · ")} />}
            {video.recording_date && <Meta icon={Calendar} label="Recording Date" value={video.recording_date} />}
            {video.location && <Meta icon={MapPin} label="Location / Dojo" value={video.location} />}
            {video.duration && <Meta icon={Clock} label="Duration" value={video.duration} />}
            {video.collection && <Meta icon={LibIcon} label="Collection" value={video.collection} />}

            {/* Provenance (only shown where documented) */}
            {(video.uploader || video.videographer || video.media_owner || video.source_archive || video.contributor || video.rights_holder || video.source) && (
              <div className="pt-4 border-t border-white/10 space-y-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#8A8F98]">Provenance</p>
                {video.source && <Meta icon={FileText} label="Source" value={video.source} />}
                {video.uploader && <Meta icon={User} label="Uploader" value={video.uploader} />}
                {video.videographer && <Meta icon={VideoIcon} label="Videographer" value={video.videographer} />}
                {video.contributor && <Meta icon={User} label="Contributor" value={video.contributor} />}
                {video.media_owner && <Meta icon={Building2} label="Media Owner" value={video.media_owner} />}
                {video.source_archive && <Meta icon={Archive} label="Source Archive" value={video.source_archive} />}
                {video.rights_holder && <Meta icon={Copyright} label="Rights Holder" value={video.rights_holder} />}
              </div>
            )}

            {/* Rights — cautious interim statement (§2) */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex gap-3">
                <Copyright size={16} className="text-[#C5A059] mt-1 shrink-0" strokeWidth={1.5} />
                <div className="min-w-0">
                  <p className="text-[10px] tracking-widest uppercase text-[#8A8F98]">Rights & Provenance</p>
                  <p className="text-[#8A8F98] text-xs leading-relaxed mt-1">{video.copyright || RIGHTS_INTERIM}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Know more about this recording? — contribution / correction queue (§8) */}
        <section className="mt-16 pt-10 border-t border-white/10">
          <div className="border border-white/10 bg-[#121418] p-6 md:p-8 max-w-3xl">
            <div className="flex items-start gap-3 mb-5">
              <Info size={20} className="text-[#C5A059] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <h2 className="font-serif text-2xl text-[#EBE9E1]">Know more about this recording?</h2>
                <p className="text-[#8A8F98] text-sm mt-1.5 leading-relaxed">
                  If you can help identify the date, location, people, source, or rights of this recording —
                  or you notice an error — please tell us. Submissions are reviewed by the archive team and
                  never change a record automatically.
                </p>
              </div>
            </div>
            {contribDone ? (
              <div data-testid="contrib-thanks" className="text-[#7FB069] text-sm border border-[#7FB069]/30 bg-[#101510] px-4 py-3">
                Thank you — your note has been sent to the archive team for review.
              </div>
            ) : !showContrib ? (
              <button data-testid="contrib-open" onClick={() => setShowContrib(true)} className="btn-ghost px-6 py-3">
                Contribute information
              </button>
            ) : (
              <form data-testid="contrib-form" onSubmit={submitContrib} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="contrib-name" value={contrib.name} onChange={(e) => setContrib({ ...contrib, name: e.target.value })}
                    placeholder="Your name (optional)" className={CONTRIB_INPUT} />
                  <input data-testid="contrib-email" type="email" value={contrib.email} onChange={(e) => setContrib({ ...contrib, email: e.target.value })}
                    placeholder="Email (optional)" className={CONTRIB_INPUT} />
                </div>
                <select data-testid="contrib-subject" value={contrib.subject} onChange={(e) => setContrib({ ...contrib, subject: e.target.value })}
                  className={CONTRIB_INPUT}>
                  <option value="correction" className="bg-[#121418]">Correction to this record</option>
                  <option value="information" className="bg-[#121418]">Additional information</option>
                  <option value="provenance" className="bg-[#121418]">Provenance / source</option>
                  <option value="rights" className="bg-[#121418]">Rights / permissions</option>
                </select>
                <textarea data-testid="contrib-message" required rows={4} value={contrib.message} onChange={(e) => setContrib({ ...contrib, message: e.target.value })}
                  placeholder="What can you tell us about this recording?" className={CONTRIB_INPUT} />
                <div className="flex gap-3">
                  <button data-testid="contrib-submit" type="submit" disabled={contribBusy} className="btn-primary px-6 py-3 disabled:opacity-60">
                    {contribBusy ? "Sending…" : "Send to archive team"}
                  </button>
                  <button type="button" onClick={() => setShowContrib(false)} className="btn-ghost px-6 py-3">Cancel</button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* Related content */}
        {relatedSections.map((sec) => (
          <section key={sec.key} className="mt-16 pt-10 border-t border-white/10">
            <h2 className="font-serif text-2xl text-[#EBE9E1] mb-6">{sec.label}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {sec.items.slice(0, 4).map((v, i) => <VideoCard key={v.id} video={v} index={i} compact />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

const Meta = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3">
    <Icon size={16} className="text-[#C5A059] mt-1 shrink-0" strokeWidth={1.5} />
    <div className="min-w-0">
      <p className="text-[10px] tracking-widest uppercase text-[#8A8F98]">{label}</p>
      <p className="text-[#EBE9E1] text-sm leading-relaxed break-words">{value}</p>
    </div>
  </div>
);

const VerificationBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  const verified = s.includes("verified") || s.includes("confirmed");
  const Icon = verified ? BadgeCheck : ShieldQuestion;
  const cls = verified
    ? "text-[#7FB069] border-[#7FB069]/40"
    : "text-[#C08A3E] border-[#C08A3E]/40";
  return (
    <span data-testid="verification-badge" className={`inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-2.5 py-1 border ${cls}`}>
      <Icon size={12} strokeWidth={2} /> {status}
    </span>
  );
};
